/**
 * IndicatorLegend - Leyenda de visualización de indicadores
 *
 * @version 1.0
 * @updated 2026-02-20
 * @module core/analysis
 *
 * @description
 * Componente flotante que muestra la leyenda de colores para el indicador activo.
 */

import { X } from 'lucide-react';
import type { ClassificationResult } from './VisualizationService';
import type { IndicatorType } from './IndicatorsService';
import './indicator-legend.css';

/**
 * Props del componente
 */
interface IndicatorLegendProps {
  /**
   * Tipo de indicador
   */
  indicatorType: IndicatorType;

  /**
   * Resultado de clasificación
   */
  classification: ClassificationResult;

  /**
   * Callback al cerrar
   */
  onClose: () => void;
}

/**
 * Obtener nombre legible del indicador
 */
function getIndicatorName(type: IndicatorType): string {
  const names: Record<IndicatorType, string> = {
    'population-per-cell': 'Población por celda',
    'trees-per-cell': 'Árboles por celda',
    'stops-per-cell': 'Paradas por celda',
    'green-area-per-cell': 'Área verde por celda',
    'vulnerability': 'Vulnerabilidad',
    'accessibility': 'Accesibilidad',
    'thermal-comfort': 'Confort térmico',
  };

  return names[type] || type;
}

/**
 * Obtener método de clasificación legible
 */
function getMethodName(method: string): string {
  const names: Record<string, string> = {
    quantiles: 'Quantiles',
    equalInterval: 'Intervalos iguales',
    naturalBreaks: 'Natural Breaks',
  };

  return names[method] || method;
}

export function IndicatorLegend({
  indicatorType,
  classification,
  onClose,
}: IndicatorLegendProps): JSX.Element {
  return (
    <div className="indicator-legend">
      <div className="legend-header">
        <h3 className="legend-title">{getIndicatorName(indicatorType)}</h3>
        <button className="legend-close" onClick={onClose} aria-label="Cerrar leyenda">
          <X size={16} />
        </button>
      </div>

      <div className="legend-method">
        <span className="method-label">Método:</span>
        <span className="method-value">{getMethodName(classification.method)}</span>
      </div>

      <div className="legend-classes">
        {classification.classes.map((cls, index) => (
          <div key={index} className="legend-class">
            <div
              className="class-color"
              style={{ backgroundColor: cls.color }}
              aria-label={`Color para clase ${cls.label}`}
            />
            <span className="class-label">{cls.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
